import { useCallback, useEffect, useState } from 'react'

// Validation
import { repRecordApprovalSchema, validateForm } from 'lib/validation'
import {
  repApprovalValidationSchema,
  useRepApprovalValidationSchema,
} from './repApprovalValidationSchema'
import useFormValidation from 'lib/hooks/useFormValidation/useFormValidation'
import { getZodError } from 'common/utils'

// Services
import { useUser } from 'app/User'

// Types
import { RepApprovalProps } from './RepApproval.types'
import { GenericObject, RepApprovalFormData } from 'common/types'

// Utils
import dayjs from 'lib/dayjs'

const useRepApprovalLogic = ({
  derivedSurgeryStatus,
  handleSetAssetStatus,
  setShowGenericAlertDialog,
  setAlertDialogDetails,
  previousRepInfo,
  setAssetStatusMutation,
}: RepApprovalProps) => {
  const { isNurse, isNurseAdmin, user, isGuest, isRep } = useUser()

  const initialFormData =
    user?.firstName && user?.lastName
      ? ({
          repName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
          isGuestRep: isGuest,
        } as RepApprovalFormData)
      : ({
          isGuestRep: isGuest,
        } as RepApprovalFormData)

  const { name: procedureStatus, workflow } = derivedSurgeryStatus
  const [isApprovalSubmitted, setIsApprovalSubmitted] = useState(false)
  const [formEvent, setFormEvent] = useState<React.FormEvent<HTMLFormElement>>(
    {} as React.FormEvent<HTMLFormElement>
  )
  const [repApprovalFormData, setRepApprovalFormData] =
    useState<RepApprovalFormData>(initialFormData)
  const [repApprovalFormErrors, setRepApprovalFormErrors] =
    useState<GenericObject>({})
  const isNurseUser = isNurse || isNurseAdmin

  const { repInfoValidationSchema, guestRepValidationSchema } =
    useRepApprovalValidationSchema()

  const repInfoHelperText =
    repApprovalFormErrors.repInfo ||
    (isGuest ? 'Format: Full Name / (XXX) XXX-XXXX' : '')

  const {
    isFormSubmitted,
    handleValidation,
    isSubmitDisabled,
    FormValidationAlertToasters,
    isFormValidated,
  } = useFormValidation({
    validationSchema: repApprovalValidationSchema,
  })

  const handleChangeFormData = (e: {
    target: { name: string; value: string }
  }) => {
    let repName = ''
    if (e.target.name === 'repInfo') {
      const rawValue = e.target.value
      if (rawValue.includes('/')) {
        const [textPart, phonePart = ''] = rawValue.split('/')

        let cleanedPhone = phonePart.replace(/\D/g, '')
        repName = textPart.trim()
        if (cleanedPhone.length > 0) {
          if (cleanedPhone.length <= 3) cleanedPhone = `(${cleanedPhone}`
          else if (cleanedPhone.length <= 6)
            cleanedPhone = `(${cleanedPhone.slice(0, 3)}) ${cleanedPhone.slice(
              3
            )}`
          else
            cleanedPhone = `(${cleanedPhone.slice(0, 3)}) ${cleanedPhone.slice(
              3,
              6
            )}-${cleanedPhone.slice(6, 10)}`
        }

        const formattedValue = `${textPart.trim()} / ${cleanedPhone}`.trim()
        e.target.value = formattedValue
      } else {
        e.target.value = rawValue
      }
    }
    setRepApprovalFormData((prevFormData) => ({
      ...prevFormData,
      // only do this if isGuest is true
      ...(isGuest && { repName: repName }),
      [e.target.name]: e.target.value,
    }))
  }

  const handleValidateForm = async () => {
    const errors = await validateForm({
      formData: repApprovalFormData,
      schema: repRecordApprovalSchema,
    })
    setRepApprovalFormErrors({ ...errors })
  }

  const handleRepApproval = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (workflow === 'no-rep') {
        const schema = isGuest
          ? guestRepValidationSchema
          : repInfoValidationSchema

        const result = schema.safeParse(repApprovalFormData)
        if (!result.success) {
          setRepApprovalFormErrors(getZodError(result.error))
          return
        }

        handleSetAssetStatus(
          'APPROVED',
          'primary',
          null,
          {
            signature: repApprovalFormData.repName,
            date: dayjs().toISOString(),
            note: repApprovalFormData?.note || null,
          },
          repApprovalFormData?.repInfo || null
        )
        return
      }

      setShowGenericAlertDialog(true)
      setAlertDialogDetails({
        title: 'Approve the products?',
        mode: 'success',
        description:
          'Approving the products will enable the nurse to submit the record to the EMR. Once the record has been submitted, no further edits will be possible.',
        primaryButtonAction: () => {
          setShowGenericAlertDialog(false)
          setRepApprovalFormData((prevFormData) => ({
            ...prevFormData,
            approvedAt: dayjs().toISOString(),
          }))
          handleSetAssetStatus(
            'APPROVED',
            'primary',
            null,
            {
              signature: repApprovalFormData.repName,
              date: dayjs().toISOString(),
              note: repApprovalFormData?.note || null,
            },
            repApprovalFormData?.repInfo || null,
            repApprovalFormData?.repName || null
          )
        },
        primaryButtonText: 'Yes, approve',
        secondaryButtonAction: () => setShowGenericAlertDialog(false),
        secondaryButtonText: 'Back',
      })
    },
    [
      handleSetAssetStatus,
      repApprovalFormData,
      repInfoValidationSchema,
      guestRepValidationSchema,
      setAlertDialogDetails,
      setShowGenericAlertDialog,
      workflow,
      isGuest,
    ]
  )

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFormEvent(e)
    handleValidation(e)
    if (isFormValidated) {
      handleRepApproval(e)
    }
  }

  useEffect(() => {
    if (isFormSubmitted && isFormValidated && !isApprovalSubmitted) {
      handleRepApproval(formEvent)
      setIsApprovalSubmitted(true)
    }
  }, [
    isFormSubmitted,
    isFormValidated,
    handleRepApproval,
    isApprovalSubmitted,
    formEvent,
  ])

  useEffect(() => {
    if (previousRepInfo) {
      handleChangeFormData({
        target: {
          name: 'repInfo',
          value: previousRepInfo,
        },
      })
    }
  }, [previousRepInfo])

  const disableSubmit =
    setAssetStatusMutation.loading ||
    ((isNurseUser || isRep) && !repApprovalFormData.repName) ||
    isSubmitDisabled

  return {
    isRep,
    isGuest,
    isNurseUser,
    procedureStatus,
    disableSubmit,
    repInfoHelperText,
    repApprovalFormData,
    repApprovalFormErrors,
    handleSubmit,
    handleValidateForm,
    handleChangeFormData,
    FormValidationAlertToasters,
  }
}

export default useRepApprovalLogic
